import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiArrowRight, FiEdit3 } from "react-icons/fi";
import { useLang } from "../../i18n/LanguageContext";
import { useAudience } from "../../audience/AudienceContext";
import { LATEST_ARTICLE } from "./latestArticle";

const STORAGE_KEY = `bm.latestArticleDismissed.${LATEST_ARTICLE.id}`;

/** How long the page gets to itself before the card slides in. */
const APPEAR_DELAY_MS = 3200;

function wasDismissed() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false; // private mode — showing it once per visit is fine
  }
}

/**
 * A floating "latest article" card on the home page. It waits a few seconds so
 * it never competes with the hero, and closing it is remembered per article —
 * the key carries the article id, so the next article shows up again even for
 * someone who dismissed this one.
 */
export default function LatestArticleCard() {
  const { t, isRTL } = useLang();
  const { hasChosen } = useAudience();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // The audience picker is a full-screen modal; don't slide in underneath it.
    if (!hasChosen || wasDismissed()) return;
    const timer = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasChosen]);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* non-fatal */ }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96, transition: { duration: 0.25 } }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          style={{
            position: "fixed",
            bottom: 24,
            insetInlineEnd: 24,
            zIndex: 4000,
            width: "min(340px, calc(100vw - 32px))",
            borderRadius: 18,
            padding: 1,
            // The gradient hairline is the eye-catcher — a lit edge rather than
            // a loud fill.
            background: "linear-gradient(140deg, rgba(129,140,248,0.75), rgba(167,139,250,0.28) 45%, rgba(52,211,153,0.35))",
            boxShadow: "0 20px 50px -16px rgba(0,0,0,0.65), 0 0 40px -18px rgba(129,140,248,0.8)",
          }}
        >
          <div style={{
            position: "relative",
            borderRadius: 17,
            background: "var(--surface)",
            backdropFilter: "blur(14px)",
            padding: "18px 18px 16px",
            overflow: "hidden",
          }}>
            {/* Soft accent bloom behind the badge */}
            <div style={{
              position: "absolute", top: -50, insetInlineStart: -30,
              width: 160, height: 160,
              background: "radial-gradient(circle, rgba(129,140,248,0.18) 0%, transparent 70%)",
              pointerEvents: "none",
            }} />

            <button
              type="button"
              onClick={dismiss}
              aria-label={t("latest.dismiss")}
              style={{
                position: "absolute", top: 10, insetInlineEnd: 10,
                width: 26, height: 26,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--surface-2)",
                color: "var(--muted-2)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                padding: 0,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = "var(--text-2)";
                e.currentTarget.style.borderColor = "var(--accent-strong)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = "var(--muted-2)";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
            >
              <FiX size={14} />
            </button>

            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "4px 10px", borderRadius: 100,
              background: "var(--accent-soft)",
              border: "1px solid var(--accent-strong)",
              fontFamily: "Inter, sans-serif", fontSize: 9.5, fontWeight: 700,
              color: "var(--accent-2)", letterSpacing: "0.1em", textTransform: "uppercase",
              marginBottom: 12,
              position: "relative",
            }}>
              <motion.span
                animate={{ opacity: [1, 0.35, 1] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                style={{ display: "flex", color: "var(--accent-2)" }}
              >
                <FiEdit3 size={11} />
              </motion.span>
              {t("latest.badge")}
            </div>

            <a
              href={LATEST_ARTICLE.link}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none", display: "block", position: "relative" }}
            >
              <div style={{
                fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 700,
                color: "var(--text)", lineHeight: 1.4, marginBottom: 7,
                letterSpacing: "-0.01em",
              }}>
                {LATEST_ARTICLE.title}
              </div>
              <div style={{
                fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--muted)",
                lineHeight: 1.55, marginBottom: 14,
              }}>
                {LATEST_ARTICLE.desc}
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 700,
                color: "var(--accent)", letterSpacing: "0.03em",
              }}>
                {t("latest.cta")}
                <FiArrowRight size={12} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
              </div>
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
